'use client';

import { useEffect, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import { CaretRightIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { useAccessContext } from '@/routing/useAccessContext';
import { canAccessAppRouteMeta } from '@/routing/access-meta';
import { useNavEntryLabel } from '@/routing/use-nav-entry-label';
import { useRouteMessage } from '@/routing/route-messages';
import { NAV_GROUP_TITLES } from '@/routing/nav-group-titles';
import {
  collapseNestedNavEntries,
  extractNavEntriesFromRoutes,
} from '@/routing/nav-from-router';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';
import { buildPinnedGroup, PINNED_GROUP_ID } from '@/routing/pinned-nav-group';
import {
  navResumeTargets,
  rememberNavLocation,
  type NavResumeTarget,
} from '@/routing/nav-resume';
import {
  mergeTaskPagesIntoNav,
  TASK_PAGES_FALLBACK_GROUP_ID,
} from '@/routing/task-pages-nav';
import { navGroupMonogram } from '@/components/sidenav/nav-group-monogram';
import { useTaskPageNavItems } from '@/components/sidenav/useTaskPageNavItems';
import { useAgentPageNavItems } from '@/components/sidenav/useAgentPageNavItems';
import { usePageSectionNavGroups } from '@/components/sidenav/usePageSectionNavGroups';
import { useBuiltInPagePlacements } from '@/api/rpc/hooks/page-sections/usePageSections';
import {
  placedBuiltInPageItems,
  unionPageSections,
} from '@/routing/built-in-page-placement';
import { useNavLayout } from '@/hooks/useNavLayout';
import { useSidebarPreferences } from '@/hooks/useSidebarPreferences';
import { getFlatRoutes } from '@/routing/flat-routes';
import { useSidebarPageMenu } from '@/components/sidenav/SidebarPageMenu';

function isPathMatch(base: string | undefined, path: string): boolean {
  if (!base) return false;
  const norm = (s: string) => (s === '/' ? '/' : s.replace(/\/+$/, ''));
  const b = norm(base);
  const p = norm(path);
  if (b === '/') return p === '/';
  return p === b || p.startsWith(`${b}/`);
}

function CollapsibleMenu({
  id,
  collapsed,
  animate,
  children,
}: {
  id: string;
  collapsed: boolean;
  animate: boolean;
  children: React.ReactNode;
}) {
  // Animate only after the user toggles a group. Hydration (cache / server
  // prefs replacing the loading state) must snap, not slide shut. When
  // collapsed, mark the subtree inert + aria-hidden so the visually-hidden
  // links leave the tab order and the a11y tree.
  return (
    <div
      id={id}
      className={cn(
        'overflow-hidden',
        animate && 'transition-[grid-template-rows] duration-200 ease-in-out',
      )}
      style={{
        display: 'grid',
        gridTemplateRows: collapsed ? '0fr' : '1fr',
      }}
    >
      <div className="min-h-0" inert={collapsed} aria-hidden={collapsed}>
        {children}
      </div>
    </div>
  );
}

/**
 * Returns the longest nav path that matches the current location. Sidebar
 * groups highlight the item whose `fullPath` equals this, so a deeper child
 * route wins over its parent. Computed once across all items, not per group.
 */
function findBestMatch(
  items: RouterNavEntry[],
  pathname: string,
): string | null {
  let best: string | null = null;
  for (const item of items) {
    const path = item.fullPath ?? '';
    if (
      isPathMatch(item.fullPath, pathname) &&
      path.length > (best?.length ?? -1)
    ) {
      best = path;
    }
  }
  return best;
}

function NavGroup({
  group,
  bestMatch,
  resumeTargets,
  collapsed,
  animate,
  onToggle,
  onItemContextMenu,
}: {
  group: SidebarNavGroup;
  bestMatch: string | null;
  /** Where opted-in items link instead of their own path (see nav-resume). */
  resumeTargets: ReadonlyMap<string, NavResumeTarget>;
  collapsed: boolean;
  animate: boolean;
  onToggle: () => void;
  /** Right-click on a row. The menu decides whether the item is a page. */
  onItemContextMenu: (
    event: React.MouseEvent,
    item: RouterNavEntry,
    group: SidebarNavGroup,
  ) => void;
}) {
  const contentId = `nav-group-${group.id}`;
  const navEntryLabel = useNavEntryLabel();
  const routeMessage = useRouteMessage();
  const gt = useGT();
  const { isMobile, state } = useSidebar();
  // A section title is its own identity — `nav.group` is the id that collapse
  // state and saved layouts key on — so it can't be encoded in place the way a
  // nav label is. NAV_GROUP_TITLES translates the code-defined ones by that id;
  // anything else (a section the user renamed) falls through and renders
  // verbatim, which is what org-configured data should do. The fallback "Pages"
  // group has no route declaring it, so it carries its own string.
  const title =
    group.id === TASK_PAGES_FALLBACK_GROUP_ID
      ? gt('Pages', {
          context:
            'sidebar group for org-configured task pages without an assigned section',
        })
      : routeMessage(NAV_GROUP_TITLES[group.title] ?? group.title);
  const iconCollapsed = state === 'collapsed' && !isMobile;
  const monogram = navGroupMonogram(title);
  return (
    <SidebarGroup className="group-data-[collapsible=icon]:not-first:border-sidebar-border/60 group-data-[collapsible=icon]:not-first:border-t">
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarGroupLabel asChild>
            <button
              type="button"
              className={cn(
                'group/label flex w-full cursor-pointer items-center select-none',
                'group-data-[collapsible=icon]:hover:bg-sidebar-accent group-data-[collapsible=icon]:hover:text-sidebar-accent-foreground',
              )}
              onClick={onToggle}
              aria-expanded={!collapsed}
              aria-controls={contentId}
              aria-label={iconCollapsed ? title : undefined}
            >
              <span className="truncate group-data-[collapsible=icon]:hidden">
                {title}
              </span>
              <span
                className={cn(
                  'hidden size-full items-center justify-center text-[11px] font-semibold tracking-tight group-data-[collapsible=icon]:inline-flex',
                  collapsed
                    ? 'text-sidebar-foreground/45'
                    : 'bg-sidebar-accent text-sidebar-accent-foreground',
                )}
                aria-hidden="true"
              >
                {monogram}
              </span>
              <CaretRightIcon
                style={{ width: 12, height: 12, minWidth: 12, minHeight: 12 }}
                className={cn(
                  'ml-0.5 shrink-0 transition-all duration-200 group-data-[collapsible=icon]:hidden',
                  collapsed ? 'rotate-0' : 'rotate-90',
                  collapsed
                    ? 'opacity-60'
                    : 'opacity-0 group-hover/label:opacity-60',
                )}
                weight="bold"
              />
            </button>
          </SidebarGroupLabel>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" hidden={!iconCollapsed}>
          {title}
        </TooltipContent>
      </Tooltip>
      <CollapsibleMenu id={contentId} collapsed={collapsed} animate={animate}>
        <SidebarMenu>
          {group.items.map((item) => {
            const label = navEntryLabel(item);
            const isActive = item.fullPath === bestMatch;
            const icon = item.meta.nav?.icon;
            const badge = item.meta.nav?.badge;
            const hasBadge = badge !== undefined && badge !== null;

            return (
              <SidebarMenuItem
                key={item.id}
                onContextMenu={(event) => onItemContextMenu(event, item, group)}
              >
                <SidebarMenuButton
                  asChild
                  tooltip={label}
                  isActive={isActive}
                  // Reserve room so the label truncates before the badge
                  // instead of running underneath it (the icon-collapsed
                  // `p-2!` override still wins when the badge is hidden).
                  className={cn(hasBadge && 'pr-9')}
                >
                  <RouterLink
                    to={
                      resumeTargets.get(item.fullPath)?.pathname ??
                      item.fullPath
                    }
                  >
                    {icon}
                    <span>{label}</span>
                  </RouterLink>
                </SidebarMenuButton>
                {hasBadge && (
                  // The primitive is pointer-events-none; badges here may
                  // carry hover content, so let them receive the pointer.
                  <SidebarMenuBadge className="pointer-events-auto px-0">
                    {badge}
                  </SidebarMenuBadge>
                )}
                {item.meta.nav?.subItems}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </CollapsibleMenu>
    </SidebarGroup>
  );
}

export function RouterNavRoutes() {
  const { pathname } = useLocation();
  const router = useRouter();
  const accessCtx = useAccessContext();
  const { collapsedGroups, assumeAllCollapsed, toggleGroup } =
    useSidebarPreferences();
  // Transitions are user-driven: enable them on the first toggle so a late
  // preference fetch can't slide sections shut after they painted open.
  const [animateCollapse, setAnimateCollapse] = useState(false);

  function handleToggle(groupId: string) {
    // Ignored while prefs are unknown — don't arm animation for a no-op, or a
    // later successful fetch would slide expanded groups open instead of snapping.
    if (assumeAllCollapsed) return;
    setAnimateCollapse(true);
    toggleGroup(groupId);
  }

  // React Compiler auto-memoizes these derived values, so they keep a stable
  // identity across renders (which is what lets useNavLayout's memo hold).
  const entries = collapseNestedNavEntries(
    extractNavEntriesFromRoutes(getFlatRoutes(router)).filter((e) =>
      canAccessAppRouteMeta(e.meta, accessCtx),
    ),
  );

  const { pinnedItems, sections: layoutSections } = useNavLayout(entries);

  const { pinnedGroup: routePinnedGroup, sections: routeSections } =
    buildPinnedGroup(pinnedItems, layoutSections);

  // Org-configurable task pages and agent pages append to their target groups
  // once loaded; router entries render immediately (an empty page list is a
  // no-op merge), so the sidebar never flickers while the queries are in
  // flight. Both kinds share one merge so they can sit in the same section
  // (built-in, org-created, or the "Pages" fallback), ordered by sortOrder
  // together. Pages targeting an org-created section land in the fallback
  // until the section titles arrive, then move into their section.
  // Built-in pages an org listed in one of its sections are added there too
  // (they also stay in their route group).
  const taskPageNavItems = useTaskPageNavItems();
  const agentPageNavItems = useAgentPageNavItems();
  const configurablePageItems = [...taskPageNavItems, ...agentPageNavItems];
  const configurablePageSections = usePageSectionNavGroups(
    configurablePageItems,
  );
  const { data: builtInPlacements = [] } = useBuiltInPagePlacements();
  // Pinned groups are searched too, so a pinned page listed in an org
  // section still shows there.
  const placedBuiltInItems = placedBuiltInPageItems(
    routePinnedGroup ? [routePinnedGroup, ...routeSections] : routeSections,
    builtInPlacements,
  );
  const mergedNav = mergeTaskPagesIntoNav({
    pinnedGroup: routePinnedGroup,
    sections: routeSections,
    taskPages: [...placedBuiltInItems, ...configurablePageItems],
    pageSections: unionPageSections(
      configurablePageSections,
      builtInPlacements,
    ),
  });
  const { pinnedGroup, sections } = mergedNav;

  const allItems = [
    ...(pinnedGroup?.items ?? []),
    ...sections.flatMap((g) => g.items),
  ];

  // Computed once across all items, then shared with every group, rather than
  // re-scanned per group.
  const bestMatch = findBestMatch(allItems, pathname);
  const resumeTargets = navResumeTargets(allItems, bestMatch, pathname);

  // Record where the reader is inside an opted-in section. After render, so
  // the links drawn for this location still point at the section's own page.
  const resumeSection = allItems.find(
    (item) => item.fullPath === bestMatch && item.meta.nav?.resumeLastVisited,
  )?.fullPath;
  useEffect(() => {
    if (resumeSection) rememberNavLocation(resumeSection, { pathname });
  }, [resumeSection, pathname]);

  return (
    <RouterNavGroups
      pinnedGroup={pinnedGroup}
      sections={sections}
      bestMatch={bestMatch}
      resumeTargets={resumeTargets}
      assumeAllCollapsed={assumeAllCollapsed}
      collapsedGroups={collapsedGroups}
      animate={animateCollapse}
      onToggle={handleToggle}
    />
  );
}

function RouterNavGroups({
  pinnedGroup,
  sections,
  bestMatch,
  resumeTargets,
  assumeAllCollapsed,
  collapsedGroups,
  animate,
  onToggle,
}: {
  pinnedGroup: SidebarNavGroup | null;
  sections: SidebarNavGroup[];
  bestMatch: string | null;
  resumeTargets: ReadonlyMap<string, NavResumeTarget>;
  assumeAllCollapsed: boolean;
  collapsedGroups: readonly string[];
  animate: boolean;
  onToggle: (groupId: string) => void;
}) {
  const groups = pinnedGroup ? [pinnedGroup, ...sections] : sections;
  const pageMenu = useSidebarPageMenu(groups);
  return (
    <>
      {pageMenu.menu}
      {pinnedGroup && (
        <NavGroup
          group={pinnedGroup}
          bestMatch={bestMatch}
          resumeTargets={resumeTargets}
          collapsed={
            assumeAllCollapsed || collapsedGroups.includes(PINNED_GROUP_ID)
          }
          animate={animate}
          onToggle={() => onToggle(PINNED_GROUP_ID)}
          onItemContextMenu={pageMenu.openAt}
        />
      )}
      {sections.map((group) => (
        <NavGroup
          key={group.id}
          group={group}
          bestMatch={bestMatch}
          resumeTargets={resumeTargets}
          collapsed={assumeAllCollapsed || collapsedGroups.includes(group.id)}
          animate={animate}
          onToggle={() => onToggle(group.id)}
          onItemContextMenu={pageMenu.openAt}
        />
      ))}
    </>
  );
}
